// The choices canvas: one question's picture options shown large, side by side,
// each with "Use this", then a "Mix or adjust" row and "Show other directions". Mixing and asking for more are
// ordinary free-text answers to the same ask (no server contract). It reads the open ask the docked panel publishes
// (question-dock.tsx), so it always shows the panel's own question, and a pick
// answers it the way the panel does.
import { useState } from 'react';
import { useWorkspace } from './context';
import { composeQuestionAnswer, emptyDraft } from './question-contract';
import { useQuestionCopy } from './question-copy';
import { pickAskOption, useOpenAsk } from './question-dock';
import { optionImage } from './question-images';

export function QuestionChoicesCanvas({ id, tab }: { id: string; tab?: string }) {
  const ws = useWorkspace();
  const shown = useOpenAsk();
  const ask = shown?.toolCallId === id ? shown : null;
  const { locale, copy } = useQuestionCopy(ask?.language);
  const index = Math.max(0, Number.parseInt(tab ?? '0', 10) || 0);
  const question = ask?.questions[index];
  const [mix, setMix] = useState('');
  if (!ask || !question)
    return (
      <div className="ws-canvas-body">
        <p className="ws-muted">{copy.choicesClosed}</p>
      </div>
    );
  const pick = (label: string) => {
    // A single one-answer question: the pick IS the answer, sent exactly as the
    // panel's Submit would send it.
    if (ask.questions.length === 1 && !question.multiSelect) {
      const answer = composeQuestionAnswer(
        ask.questions,
        [{ ...emptyDraft(), picked: [label] }],
        copy.noPreference,
      );
      ws.send(answer, ws.turnContext, true, null);
      return;
    }
    // Otherwise it is ticked in the panel, which carries on with the rest.
    pickAskOption(ask, index, label);
    ws.openCanvas(null);
  };
  // A mix or an adjustment is this question's free-text answer, sent as the panel would send it.
  const mixed = () => {
    const text = mix.trim();
    if (!text) return;
    const answer = composeQuestionAnswer(
      [question],
      [{ ...emptyDraft(), other: text, otherPicked: true }],
      copy.noPreference,
    );
    ws.send(answer, ws.turnContext, true, null);
    setMix('');
  };
  return (
    <div className="ws-canvas-body ws-choices" lang={locale}>
      <div className="ws-eyebrow">{question.header}</div>
      <h1>{question.question}</h1>
      <div className="ws-choices-grid">
        {question.options.map((option) => {
          const image = optionImage(ask.images, option.previewImage);
          return (
            <figure key={option.label} className="ws-choice">
              <span className="ws-choice-frame">
                {image ? (
                  <img src={image} alt={option.label} />
                ) : (
                  <span className="ws-choice-blank" aria-hidden="true" />
                )}
              </span>
              <figcaption>
                <span className="ws-choice-label">{option.label}</span>
                {option.description && <small>{option.description}</small>}
              </figcaption>
              <button
                type="button"
                className="ws-button primary"
                aria-label={`${copy.useThis}: ${option.label}`}
                onClick={() => pick(option.label)}
              >
                {copy.useThis}
              </button>
            </figure>
          );
        })}
      </div>
      <form
        className="ws-choices-mix"
        data-slot="choices-mix"
        onSubmit={(event) => {
          event.preventDefault();
          mixed();
        }}
      >
        <input
          type="text"
          className="ws-choices-mix-input"
          value={mix}
          placeholder={copy.mixPlaceholder}
          aria-label={copy.mixPlaceholder}
          onChange={(event) => setMix(event.target.value)}
        />
        <button type="submit" className="ws-button primary" disabled={!mix.trim()}>
          {copy.mixSend}
        </button>
        <button
          type="button"
          className="ws-button"
          onClick={() => ws.send(copy.moreDirectionsMessage, ws.turnContext, true, null)}
        >
          {copy.moreDirections}
        </button>
      </form>
    </div>
  );
}
